<template>
  <action-dispatcher
    action="lessons/getNotations"
    class="d-flex flex-column h-100"
    :parameters="$route.params.lesson"
    @success="success"
  >
    <form class="tab-lesson-notations-component pt-2" @submit.prevent="save">
      <div v-if="!notations.length" class="empty-state">
        <div class="empty-icon">
          <i class="fa-regular fa-note-sticky"></i>
        </div>
        <p class="empty-title">Nenhuma anotação ainda</p>
        <p class="empty-description">
          Faça anotações durante a aula para revisar depois.
          Elas serão salvas com o timestamp do vídeo!
        </p>
        <div class="empty-tip">
          <i class="fa-solid fa-lightbulb"></i>
          <span>Dica: Ao digitar, o vídeo pausará automaticamente</span>
        </div>
      </div>
      <div class="notations-list custom-thin-scrollbar flex-fill mb-2">
        <div
          v-for="(note, i) in notations"
          :key="`note-${i}`"
          class="notation-item p-2 mb-2"
          @click="seekTo(note)"
        >
          <div class="d-flex align-items-start">
            <div class="flex-fill duration">
              <small> {{ formatDuration(note.current_lesson_duration) }}</small>
            </div>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none menu-icon btn-sm p-0"
              no-caret
              left
            >
              <template #button-content>
                <i class="icon-options text-secondary"></i>
              </template>
              <b-dropdown-item
                @click="edit(note)"
                link-class="d-item font-weight-normal"
              >
                <i class="fa-solid fa-pen text-dark"></i> Editar nota
              </b-dropdown-item>
              <b-dropdown-item
                @click="remove(note)"
                link-class="d-item font-weight-normal"
              >
                <i class="fa-solid fa-trash text-dark"></i> Apagar nota
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div v-if="note.tag && getTagInfo(note.tag)" class="note-tag-badge" :class="`tag-${note.tag}`">
            <span>{{ getTagInfo(note.tag).emoji }}</span>
            <span>{{ getTagInfo(note.tag).label }}</span>
          </div>
          <span class="note-content">
            {{ note.text }}
          </span>
        </div>
      </div>
      <div class="notation-input-area">
        <div class="quick-tags">
          <button
            type="button"
            v-for="tag in quickTags"
            :key="tag.id"
            class="quick-tag-btn"
            :class="{ active: selectedTag === tag.id }"
            @click="toggleTag(tag.id)"
          >
            <span class="tag-emoji">{{ tag.emoji }}</span>
            <span class="tag-label">{{ tag.label }}</span>
          </button>
        </div>
        <div class="form-group d-flex align-items-center pr-2">
          <input
            type="text"
            v-model="text"
            class="form-control flex-fill"
            placeholder="Nova anotação...."
            @input="$emit('pause')"
            @blur="$emit('play')"
            @keyup.enter="save"
          />
          <button class="btn text-primary btn-sm" type="submit">
            <b-spinner v-if="loading" small class="my-1" />
            <span v-else>Salvar</span>
          </button>
        </div>
      </div>

      <b-modal
        v-model="editing"
        :modal-class="`theme-${$setup.theme}-modals`"
        size="md"
        scrollable
        hide-footer
        hide-header
        centered
      >
        <div class="edit-note-modal">
          <h6 class="text-center mb-4">Editar nota</h6>
          <button class="btn icon-close" @click="cancelEdit" />
          <div v-if="edited_note" class="form-group mb-4">
            <textarea
              type="text"
              v-model="edited_note.text"
              class="form-control flex-fill"
              placeholder="Nova anotação...."
            >
            </textarea>
          </div>
          <div class="d-flex align-items-center justify-content-end">
            <button class="btn btn-outline-secondary mr-4" @click="cancelEdit">
              Cancelar
            </button>
            <button class="btn btn-primary" type="button" @click="save">
              <b-spinner v-if="loading" small />
              <span v-else>Salvar</span>
            </button>
          </div>
        </div>
      </b-modal>
    </form>
  </action-dispatcher>
</template>
<script>
import { duration_seconds_to_humanized } from "@/functions/helpers";
export default {
  props: {
    lesson: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      notations: [],
      edited_note: null,
      editing: false,
      loading: false,
      text: "",
      selectedTag: null,
      quickTags: [
        { id: "importante", emoji: "⭐", label: "Importante" },
        { id: "revisar", emoji: "🔄", label: "Revisar" },
        { id: "duvida", emoji: "❓", label: "Dúvida" },
        { id: "ideia", emoji: "💡", label: "Ideia" }
      ]
    };
  },
  methods: {
    success({ results }) {
      this.notations = results;
    },
    formatDuration(duration) {
      return duration_seconds_to_humanized(duration);
    },
    seekTo(note) {
      if (window.player) {
        window.player.currentTime = note.current_lesson_duration;
      }
    },
    toggleTag(tagId) {
      this.selectedTag = this.selectedTag === tagId ? null : tagId;
    },
    getTagInfo(tagId) {
      return this.quickTags.find(t => t.id === tagId);
    },
    async save() {
      try {
        this.loading = true;

        if (this.edited_note) {
          if (!this.edited_note.text || this.edited_note.text.length == 0) {
            this.$message.warning("Digite o texto da sua anotação");
            return;
          }
          await this.$store.dispatch("lessons/putNotations", this.edited_note);
          this.edited_note = null;
          this.editing = false;
        } else {
          if (!this.text || this.text.length == 0) {
            this.$message.warning("Digite o texto da sua anotação");
            return;
          }

          let current_lesson_duration = 0;
          if (window.player) {
            current_lesson_duration = window.player.currentTime;
          }
          const payload = {
            lesson: this.$route.params.lesson,
            current_lesson_duration: parseInt(current_lesson_duration),
            text: this.selectedTag ? `[${this.selectedTag}] ${this.text}` : this.text,
            tag: this.selectedTag
          };
          await this.$store.dispatch("lessons/postNotations", payload);
        }

        this.text = "";
        this.selectedTag = null;
        await this.$store
          .dispatch("lessons/getNotations", this.$route.params.lesson)
          .then(this.success);
      } catch (e) {
        this.$message.error("Não foi possível salvar sua anotação.");
      } finally {
        this.loading = false;
      }
    },
    edit(note) {
      this.edited_note = { ...note };
      this.editing = true;
    },
    cancelEdit() {
      this.edited_note = null;
      this.editing = false;
    },
    async remove(note) {
      await this.$store.dispatch("lessons/deleteNotations", note.id).then();
      await this.$store
        .dispatch("lessons/getNotations", this.$route.params.lesson)
        .then(this.success);
    }
  }
};
</script>
<style lang="scss" scoped>
.tab-lesson-notations-component {
  display: flex;
  flex-direction: column;
  height: -webkit-fill-available;

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 20px;
    text-align: center;

    .empty-icon {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: rgba(171, 133, 229, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;

      i {
        font-size: 28px;
        color: #ab85e5;
      }
    }

    .empty-title {
      font-size: 15px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 8px;
    }

    .empty-description {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.5);
      line-height: 1.5;
      margin-bottom: 16px;
      max-width: 220px;
    }

    .empty-tip {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 14px;
      background: rgba(228, 175, 55, 0.1);
      border-radius: 8px;
      border: 1px solid rgba(228, 175, 55, 0.2);

      i {
        color: #e4af37;
        font-size: 14px;
      }

      span {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.7);
        line-height: 1.4;
      }
    }
  }

  .notations-list {
    overflow-y: scroll;
    height: 100px;
    @media screen and (max-width: 991px) {
      height: 300px;
    }
  }

  .form-group {
    border-radius: 10px;
    .form-control {
      background: transparent !important;
      box-shadow: none;
      outline: none;
      border: none !important;
      height: 100%;
      resize: none;
    }
  }

  .duration {
    font-style: normal;
    line-height: 150%;
    font-weight: 400;
  }
  .notation-input-area {
    .quick-tags {
      display: flex;
      gap: 6px;
      margin-bottom: 10px;
      flex-wrap: wrap;

      .quick-tag-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 6px 10px;
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.05);
        color: rgba(255, 255, 255, 0.6);
        font-size: 11px;
        cursor: pointer;
        transition: all 0.2s ease;

        .tag-emoji {
          font-size: 12px;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.25);
        }

        &.active {
          background: rgba(171, 133, 229, 0.2);
          border-color: #ab85e5;
          color: #ab85e5;
        }
      }
    }
  }

  .notation-item {
    cursor: pointer;
    font-size: 14px;
    border-radius: 8px;
    line-break: anywhere;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateX(4px);
      box-shadow: -4px 0 0 #ab85e5;
    }

    small {
      font-size: 12px;
    }

    .note-tag-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 500;
      margin-bottom: 6px;

      &.tag-importante {
        background: rgba(228, 175, 55, 0.15);
        color: #e4af37;
      }

      &.tag-revisar {
        background: rgba(62, 86, 172, 0.15);
        color: #6b8cff;
      }

      &.tag-duvida {
        background: rgba(224, 104, 114, 0.15);
        color: #e06872;
      }

      &.tag-ideia {
        background: rgba(171, 133, 229, 0.15);
        color: #ab85e5;
      }
    }

    .note-content {
      font-size: 14px;
      font-weight: 400;
      line-height: 150%;
      display: block;
    }
  }

  .icon-close {
    position: absolute;
    top: 2%;
    right: 0%;
  }

  ::v-deep .dropdown-menu {
    box-shadow: none !important;
    border: none !important;
    background: #fff !important;
    top: -35px !important;
    left: -30px !important;
  }

  .d-item,
  .dropdown-menu .dropdown-item {
    font-weight: 400 !important;
    font-size: 14px !important;
    i {
      color: #000 !important;
      font-size: 14px;
    }

    .dropdown-toggle {
      padding: 0;
      min-height: 5px;
      min-width: 5px;
    }
  }
}
</style>
